import { LABELS_TYPE } from '@atlassian/jira-platform-field-config';
import {
	URL_RENDERER,
	DATE_RENDERER,
	DURATION_RENDERER,
	NUMBER_RENDERER,
	STATUS_RENDERER,
	TEXT_RENDERER,
	HTML_RENDERER,
	THUMBNAIL_SYSTEM_FIELD_ID,
} from './constants';

export type FieldRender =
	| typeof DATE_RENDERER
	| typeof DURATION_RENDERER
	| typeof NUMBER_RENDERER
	| typeof STATUS_RENDERER
	| typeof TEXT_RENDERER
	| typeof HTML_RENDERER
	| typeof URL_RENDERER;

export interface BaseExtraField {
	readonly id: string;
	readonly label: string;
	readonly renderer: FieldRender;
}

export interface ExtraFieldHtml extends BaseExtraField {
	readonly renderer: typeof HTML_RENDERER;
	readonly html: string;
}

export interface ExtraFieldDate extends BaseExtraField {
	readonly renderer: typeof DATE_RENDERER;
	readonly html: string;
	readonly text: string;
}

export interface ExtraFieldText extends BaseExtraField {
	readonly renderer:
		| typeof TEXT_RENDERER
		| typeof STATUS_RENDERER
		| typeof DURATION_RENDERER
		| typeof NUMBER_RENDERER
		| typeof URL_RENDERER;
	readonly text: string;
}

export type ExtraField = ExtraFieldHtml | ExtraFieldText | ExtraFieldDate;

function isExtraField(field: unknown, rendererTypes: FieldRender[] = []): field is ExtraFieldHtml {
	if (field !== null && typeof field === 'object' && 'renderer' in field) {
		return rendererTypes.some((rendererType) => field?.renderer === rendererType);
	}
	return false;
}

export function isExtraFieldHtml(field: unknown): field is ExtraFieldHtml {
	return isExtraField(field, [HTML_RENDERER]) && 'html' in field;
}

export function isExtraFieldDate(field: unknown): field is ExtraFieldDate {
	return isExtraField(field, [DATE_RENDERER]);
}

export function isExtraFieldLabel(field: unknown): field is ExtraFieldText {
	return isExtraField(field, [TEXT_RENDERER]) && field.id === LABELS_TYPE;
}

export function isExtraFieldThumbnail(field: unknown): field is ExtraFieldHtml {
	return isExtraFieldHtml(field) && field.id === THUMBNAIL_SYSTEM_FIELD_ID;
}

export function isExtraFieldText(field: unknown): field is ExtraFieldText {
	return isExtraField(field, [
		TEXT_RENDERER,
		NUMBER_RENDERER,
		DURATION_RENDERER,
		STATUS_RENDERER,
		URL_RENDERER,
	]);
}
